import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Medal, Trophy, Users } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface EventAchievement {
  kind: "torneo" | "americano";
  scope: "interno";
  eventId: string;
  eventName: string;
  leagueName: string;
  result: string;
  date: string | null;
}

interface EventHistoryItem {
  kind: "torneo" | "americano";
  eventId: string;
  eventName: string;
  leagueName: string;
  result: string | null;
  status: string;
  date: string | null;
}

interface EventResultsResponse {
  achievements: EventAchievement[];
  history: EventHistoryItem[];
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return format(new Date(iso), "MMM yyyy", { locale: es });
  } catch {
    return "";
  }
}

const STATUS_LABELS: Record<string, string> = {
  completed: "Finalizado",
  in_progress: "En curso",
  open: "Abierto",
};

/**
 * Tournament-results "Logros" section for a player profile.
 *
 * Internal achievements (league Torneos Cerrados + Americanos) render as
 * SMALL, secondary badges with the league name and NO club logo. The layout
 * intentionally leaves the top spot for future prominent "external"
 * achievements (large badge + club logo) — none exist today.
 */
export function EventAchievementsSection({ userId }: { userId: string | null | undefined }) {
  const { data } = useQuery<EventResultsResponse>({
    queryKey: [`/api/users/${userId}/event-results`],
    queryFn: async () => {
      const res = await fetch(`/api/users/${userId}/event-results`);
      if (!res.ok) throw new Error("Failed to fetch event results");
      return res.json();
    },
    enabled: !!userId,
  });

  const achievements = data?.achievements ?? [];
  const history = data?.history ?? [];

  if (achievements.length === 0 && history.length === 0) return null;

  return (
    <Card data-testid="card-event-achievements">
      <CardContent className="pt-6 space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Medal className="h-4 w-4 text-amber-500" />
          Logros
        </h3>

        {achievements.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="list-event-achievement-badges">
            {achievements.map((a) => (
              <div
                key={`${a.kind}-${a.eventId}`}
                className="flex items-center gap-2 rounded-lg border bg-muted/40 px-2.5 py-1.5"
                data-testid={`badge-event-achievement-${a.eventId}`}
              >
                {a.kind === "torneo" ? (
                  <Trophy className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                ) : (
                  <Users className="h-3.5 w-3.5 text-sky-500 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-semibold leading-tight truncate max-w-[180px]">
                    {a.result} · {a.eventName}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight truncate max-w-[180px]">
                    {a.leagueName}
                    {a.date ? ` · ${formatDate(a.date)}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {history.length > 0 && (
          <div className="space-y-1.5" data-testid="list-event-history">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Eventos jugados
            </p>
            {history.map((h) => (
              <div
                key={`${h.kind}-${h.eventId}`}
                className="flex items-center justify-between gap-2 text-xs py-1 border-b last:border-b-0 border-border/60"
                data-testid={`row-event-history-${h.eventId}`}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  {h.kind === "torneo" ? (
                    <Trophy className="h-3 w-3 text-muted-foreground shrink-0" />
                  ) : (
                    <Users className="h-3 w-3 text-muted-foreground shrink-0" />
                  )}
                  <span className="truncate font-medium">{h.eventName}</span>
                  <span className="text-muted-foreground truncate hidden sm:inline">
                    · {h.leagueName}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {h.result ? (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4.5">
                      {h.result}
                    </Badge>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">
                      {STATUS_LABELS[h.status] ?? h.status}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
